import { Trans, t } from '@lingui/macro';
import { ChevronDown, Menu } from 'lucide-react';
import { Share2, X, Search, ArrowLeft, PanelRight, Star, SlidersHorizontal, Filter, ChevronRight, ChevronLeft } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';
import { useHistory } from '../../hooks/useRouterCompat';
import { useAppUIContext } from '../../context/AppUIContext';
import { Tran } from '../../locales/Tran';
import { Room } from '../../models/Room';
import { RoomCall } from '../../models/RoomCall';
import { Workspace } from '../../models/Workspace';
import { FilterType } from '../../models/local/Types';
import { Fire } from '../../services/FireClient';
import { My } from '../../services/My';
import { MyNotifs } from '../../services/MyNotifs';
import { twil } from '../../services/TwilioHelper';
import CallHeaderPreview from '../components/CallHeaderPreview';
import FilterPopover from '../components/FilterPopover';
import MyRoomOptionModal from '../components/MyRoomOptionModal';
import WorkspaceMiniTitle from '../components/WorkspaceMiniTitle';
import { Button } from '../../components/ui/button';
import { ButtonGroup } from '../../components/ui/button-group';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';
import { useResponsive, showRightMenu } from '../../hooks/useResponsive';

interface MobileHeaderActionsProps {
  onSearchModeChange?: (mode: boolean) => void;
  gotoFavs: () => void;
  gotoShares: () => void;
  isFavoritesMode: boolean;
  isPersonalRoom: boolean;
  filter: FilterType;
  roomId: string;
}

const MobileHeaderActions: React.FC<MobileHeaderActionsProps> = ({
  onSearchModeChange,
  gotoFavs,
  gotoShares,
  isFavoritesMode,
  isPersonalRoom,
  filter,
  roomId
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-collapse when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    }
    if (isExpanded) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isExpanded]);

  const showFilter = !isPersonalRoom && filter == 'none';


  // Styles
  // relative added to ensure z-index always works, preventing the "pop up" glitch
  const btnCommonClass = "h-9 w-9 transition-all duration-200 border-gray-200 bg-white relative";
  const collapsedGroupClass = " [&>*]:shadow-sm";
  const expandedGroupClass = "animate-in fade-in zoom-in-95 origin-right duration-200";

  return (
    <ButtonGroup
      ref={containerRef}
      className={cn(
        "transition-all duration-300 ease-in-out bg-white",
        isExpanded ? expandedGroupClass : collapsedGroupClass
      )}
    >
      {/* Toggle Button */}
      <Button
        variant="icon-box"
        onClick={(e) => {
          e.stopPropagation();
          setIsExpanded(!isExpanded);
        }}
        className={cn(
          btnCommonClass,
          "",
          !isExpanded ? "z-50" : "z-50"
        )}
      >
        {isExpanded ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
      </Button>

      {/* Favorites Button */}
      <Button
        variant="outline"
        size="icon"
        onClick={isExpanded ? gotoFavs : undefined}
        className={cn(
          btnCommonClass,
          isFavoritesMode && "bg-orange-50",
          !isExpanded ? "pointer-events-none hidden" : "z-30"
        )}
      >
        <Star className={cn(
          "h-4 w-4",
          isFavoritesMode ? "fill-orange-500 text-orange-500" : "text-orange-500"
        )} />
      </Button>

      {/* Share Button */}
      <Button
        variant="outline"
        size="icon"
        onClick={isExpanded ? gotoShares : undefined}
        className={cn(
          btnCommonClass,
          !isExpanded ? "pointer-events-none hidden" : "z-20"
        )}
      >
        <Share2 className="h-4 w-4 text-orange-500" />
      </Button>

      {/* Filter Button */}
      {showFilter && (
        <FilterPopover
          roomId={roomId}
          className={cn(
            btnCommonClass,
            !isExpanded ? "pointer-events-none hidden" : "z-10"
          )}
          variant="outline"
          size="icon"
        />
      )}
      {/* Search Button */}
      <Button
        variant="outline"
        size="icon"
        onClick={() => onSearchModeChange?.(true)}
        className={cn(
          btnCommonClass,
          !isExpanded ? "z-0" : "z-40"
        )}
      >
        <Search className="h-4 w-4 text-orange-500" />
      </Button>

    </ButtonGroup>
  );
};

interface RoomHeaderProps {
  room: Room;
  type: FilterType;
  inProgress: boolean;
  isSearchMode?: boolean;
  searchQuery?: string;
  isFavoritesMode?: boolean;
  onSearchModeChange?: (mode: boolean) => void;
  onFavoritesModeChange?: (mode: boolean) => void;
  onSearchQueryChange?: (query: string) => void;
  onSearchSubmit?: () => void;
  onRightMenuToggle?: () => void;
}

const RoomHeader: React.FC<RoomHeaderProps> = ({
  room,
  inProgress,
  isSearchMode = false,
  searchQuery = "",
  isFavoritesMode = false,
  onSearchModeChange,
  onFavoritesModeChange,
  onSearchQueryChange,
  onSearchSubmit,
  onRightMenuToggle
}) => {
  const { filter, setFilter, workspaces, setEnterType, setShowFilesModal, setIsMobileMenuOpen, activeCalls } = useAppUIContext();
  const history = useHistory();
  const [workspace, setWorkspace] = useState<Workspace>();
  const call = activeCalls.get(room.id);
  const [counter, setCounter] = useState(0);
  const { isSmallMobile, isMobileOrTablet } = useResponsive();
  const [isRoomModalOpen, setIsRoomModalOpen] = useState(false);

  const isInCall = twil.isConnected() && twil.getRoomId() == room.id;
  const isPersonalRoom = room.visibility === 'personal';
  const typeTrans = Tran.filterType(filter);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  useEffect(() => {
    setFilter('none');
    let unsubNotifs = MyNotifs.subscribeToNotificationUpdates((_) => {
      setCounter(MyNotifs.NrOfNew);
    });

    // 워크스페이스 캐시 우선 활용
    const ws = workspaces.find(w => w.id === room.workspaceId);
    if (ws) {
      setWorkspace(ws);
    } else {
      My.getOrFetchWorkspace(room.workspaceId, workspaces).then(setWorkspace);
    }

    return () => { unsubNotifs(); }
  }, [room.id, room.workspaceId, workspaces]);

  function isCallOngoing() {
    return RoomCall.isActive(call);
  }

  function gotoShares() {
    setEnterType('link');
    setShowFilesModal(true);
  }

  function gotoFavs() {
    // 토글 기능: 현재 즐겨찾기 모드면 해제, 아니면 활성화
    onFavoritesModeChange?.(!isFavoritesMode);
  }

  return (
    <>
      <header className="sticky top-0 z-40 bg-white border-b border-[#E5E5E5] min-w-0 relative overflow-hidden">
        <div className="flex items-center px-2 md:px-4 py-2 md:py-4 gap-2 md:gap-4 w-full">
          {/* Left - Menu Button */}
          <div className="shrink-0 lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              data-cy='menu'
              onClick={() => setIsMobileMenuOpen(true)}
              className="relative"
            >
              <Menu className="h-5 w-5" />
              {!!counter && (
                <Badge variant="destructive" className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 rounded-full text-[10px]">
                  {counter}
                </Badge>
              )}
            </Button>
          </div>

          {/* Center - Title or Search Input */}
          <div className="flex-1 min-w-0">
            {isSearchMode ? (
              // 검색 입력 모드
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  onClick={() => {
                    onSearchModeChange?.(false);
                    onSearchQueryChange?.("");
                  }}
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <Input
                  type="text"
                  variant="basic"
                  placeholder={t`Search messages and file names...`}
                  value={searchQuery}
                  onChange={(e) => onSearchQueryChange?.(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && searchQuery.trim()) {
                      e.preventDefault();
                      onSearchSubmit?.();
                    }
                  }}
                  className="flex-1 my-[4.5px]"
                  autoFocus
                />
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    onClick={() => onSearchQueryChange?.("")}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ) : (
              // 일반 모드 - 제목 표시
              <>
                {isPersonalRoom ? (
                  <h1 className="text-lg font-semibold truncate">
                    <Trans>My Room</Trans>
                  </h1>
                ) : (
                  <>
                    <div className="flex items-center gap-2 max-w-full">
                      <span className="truncate font-medium text-md">
                        {Room.getRoomNameWithSharp(room, My.user.id)}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0 text-gray-500 hover:bg-orange-100"
                        onClick={() => setTimeout(() => setIsRoomModalOpen(true), 200)}
                        data-cy="setting"
                      >
                        <SlidersHorizontal className="h-4 w-4 text-orange-400" />
                      </Button>
                      {typeTrans && (
                        <Badge
                          variant="secondary"
                          className="cursor-pointer flex items-center gap-1 flex-shrink-0"
                          onClick={() => setFilter('none')}
                        >
                          <X className="h-3 w-3" />
                          <span className="text-xs whitespace-nowrap">{typeTrans}</span>
                        </Badge>
                      )}
                    </div>
                    {workspace && <WorkspaceMiniTitle workspace={workspace} />}
                  </>
                )}
              </>
            )}
          </div>

          {/* Right - Action Buttons */}
          <div className="flex items-center gap-2 shrink-0">
            {!isSearchMode && (
              <>
                {/* Desktop (>= 480px) - All action buttons */}
                {!isSmallMobile && (
                  <>
                    <Button
                      variant="icon-box"
                      onClick={() => onSearchModeChange?.(true)}
                    >
                      <Search className="h-5 w-5 text-orange-500" />
                    </Button>
                    <Button
                      variant="icon-box"
                      onClick={gotoFavs}
                      className={cn(isFavoritesMode && "bg-orange-50 border-orange-300")}
                    >
                      <Star className={cn(
                        "h-5 w-5",
                        isFavoritesMode ? "fill-orange-500 text-orange-500" : "text-orange-500"
                      )} />
                    </Button>
                    <Button
                      variant="icon-box"
                      onClick={gotoShares}
                    >
                      <Share2 className="h-5 w-5 text-orange-500" />
                    </Button>
                    {!isPersonalRoom && filter == 'none' && (
                      <FilterPopover roomId={room.id} />
                    )}
                  </>
                )}

                {/* Mobile (< 480px) - Expandable Actions */}
                {isSmallMobile && (
                  <MobileHeaderActions
                    onSearchModeChange={onSearchModeChange}
                    gotoFavs={gotoFavs}
                    gotoShares={gotoShares}
                    isFavoritesMode={isFavoritesMode}
                    isPersonalRoom={isPersonalRoom}
                    filter={filter}
                    roomId={room.id}
                  />
                )}

                {/* Right Menu Toggle (< 1200px) */}
                {isMobileOrTablet && (
                  <Button
                    variant="icon-box"
                    onClick={() => onRightMenuToggle?.()}
                    data-cy="menu"
                  >
                    <PanelRight className="h-5 w-5 text-orange-500" />
                  </Button>
                )}
              </>
            )}
          </div>

          {/* Progress Bar */}
        </div>
        {inProgress && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 overflow-hidden">
            <div className="h-full w-full bg-primary animate-pulse" />
          </div>
        )}

        {/* Call Header Preview */}
        {call && !isInCall && <CallHeaderPreview room={room} call={call} />}
      </header>

      {/* Room Options Modal */}
      <MyRoomOptionModal
        room={room}
        workspace={workspace}
        isOpen={isRoomModalOpen}
        onOpenChange={setIsRoomModalOpen}
      />
    </>
  )
};

export default RoomHeader;
